import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Procurement from './pages/Procurement';
import Onboarding from './pages/Onboarding';
import Timesheets from './pages/Timesheets';
import Transportation from './pages/Transportation';
import Facilities from './pages/Facilities';
import Events from './pages/Events';
import MealSelection from './pages/MealSelection';
import UserManagement from './pages/UserManagement';
import Layout from './components/Layout';
import { Toaster } from './components/ui/sonner';
import { PermissionsProvider } from './contexts/PermissionsContext';
import './App.css';

// Wrapper component to handle logout with navigation
function AppContent({ user, setUser, handleLogin }) {
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/login', { replace: true });
  };

  const hasModuleAccess = (module) => {
    if (!user || !user.role) return false;
    if (user.role === 'super_admin' || user.role === 'admin') return true;
    const moduleAccess = user.module_access || user.modules || [];
    return moduleAccess.includes(module);
  };

  return (
    <Routes>
      {/* Public route for meal selection */}
      <Route path="/meal-selection/:token" element={<MealSelection />} />
      
      {/* Auth routes */}
      <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" replace />} />
      <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" replace />} />
      <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/" replace />} />
      
      <Route path="/" element={user ? <Layout user={user} onLogout={handleLogout} hasModuleAccess={hasModuleAccess} /> : <Navigate to="/login" replace />}>
        <Route index element={<Dashboard />} />
        {hasModuleAccess('procurement') && (
          <Route path="procurement" element={<Procurement user={user} />} />
        )}
        {hasModuleAccess('onboarding') && (
          <Route path="onboarding" element={<Onboarding user={user} />} />
        )}
        {hasModuleAccess('timesheets') && (
          <Route path="timesheets" element={<Timesheets user={user} />} />
        )}
        {hasModuleAccess('transportation') && (
          <Route path="transportation" element={<Transportation user={user} />} />
        )}
        {hasModuleAccess('facilities') && (
          <Route path="facilities" element={<Facilities user={user} />} />
        )}
        {hasModuleAccess('events') && (
          <Route path="events" element={<Events user={user} />} />
        )}
        {(user && (user.role === 'admin' || user.role === 'super_admin')) && (
          <Route path="users" element={<UserManagement user={user} />} />
        )}
      </Route>
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  if (loading) {
    return <div className="flex h-screen items-center justify-center bg-[#FBF8EF]">
      <div className="animate-spin w-8 h-8 border-4 border-[#B2560D] border-t-transparent rounded-full"></div>
    </div>;
  }

  return (
    <PermissionsProvider user={user}>
      <div className="App">
        <BrowserRouter>
          <AppContent user={user} setUser={setUser} handleLogin={handleLogin} />
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </PermissionsProvider>
  );
}

export default App;
